import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { api, type CatalogEntry } from "../api";
import { Empty, ErrorMessage, Loading } from "../components";
import { priceLines, perMillion, providerLabel, TIER_LABEL, tokenWindow } from "../modelsFormat";

/** The Models page (TAI-2331): what this person can use, aliases first. Filtered by the server. */
export function ModelsPage() {
  const catalog = useQuery({ queryKey: ["catalog"], queryFn: api.catalog });

  if (catalog.isPending) return <Loading />;
  if (catalog.error) return <ErrorMessage error={catalog.error} />;
  const c = catalog.data;
  if (c.aliases.length + c.routers.length + c.models.length === 0) {
    return <Empty title="No models are available to you" message="Ask in the AI Gateway Early Adopters channel in Teams which models your account should reach." />;
  }

  return (
    <div className="stack gap-large">
      {c.aliases.length > 0 && (
        <Section icon="lightbulb" title="Aliases (recommended)" intro={c.intro.aliases}>
          <table className="forge-data-table">
            <thead><tr><th>Alias</th><Limits /><th>Price per 1M tokens</th><th>Best uses</th></tr></thead>
            <tbody>
              {c.aliases.map((e) => (
                <tr key={e.name}>
                  <td>
                    <div className="mono"><b>{e.name}</b></div>
                    <div className="mono muted id-wrap">now {e.target}</div>
                  </td>
                  <LimitCells e={e} />
                  <PriceCell e={e} />
                  <td className="wrap-ok best-uses">{e.bestUses}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      {c.routers.length > 0 && (
        <Section icon="alt_route" title="Routers" intro={c.intro.routers}>
          {c.routers.map((r) => (
            <div key={r.name} className="stack gap-xsmall mb-medium">
              <p className="forge-typography--body2 card-title"><b className="mono">{r.name}</b>{r.bestUses && <> · {r.bestUses}</>}</p>
              <table className="forge-data-table">
                <thead><tr><th>When the prompt is</th><th>It uses</th><th>Price per 1M tokens</th></tr></thead>
                <tbody>
                  {r.routes.map((t) => (
                    <tr key={t.tier}>
                      <td>{TIER_LABEL[t.tier] ?? t.tier}</td>
                      <td className="mono">{t.models.map((m) => m.name).join(", ")}</td>
                      <td>{t.models.map((m) => (m.price ? `${perMillion(m.price.input)} in · ${perMillion(m.price.output)} out` : "not published")).join("; ")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="muted">
                You pay the price of the model it picks; the router itself adds nothing.
                {r.defaultModel && <> If it cannot decide, it uses <span className="mono">{r.defaultModel}</span>.</>}
              </p>
            </div>
          ))}
        </Section>
      )}

      {c.models.length > 0 && (
        <Section icon="memory" title="Models" intro={c.intro.models}>
          <table className="forge-data-table">
            <thead><tr><th>Model</th><Limits /><th>Price per 1M tokens</th><th>Supports</th><th>Best uses</th></tr></thead>
            <tbody>
              {c.models.map((e) => (
                <tr key={e.name}>
                  <td>
                    <div className="mono id-wrap">{e.name}</div>
                    <div className="muted">{providerLabel(e.provider) ?? "provider unknown"}</div>
                  </td>
                  <LimitCells e={e} />
                  <PriceCell e={e} />
                  <td className="wrap-ok">{e.capabilities.join(", ")}</td>
                  <td className="wrap-ok best-uses">
                    {e.aliases.length > 0 && <div>Use the alias <b className="mono">{e.aliases.join(", ")}</b> instead.</div>}
                    {e.bestUses && <div className={e.aliases.length ? "muted" : undefined}>{e.bestUses}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      <p className="muted">
        Prices are the gateway's list prices in US dollars. Context window is the most a model reads in one request;
        max output is the longest reply it can write.
      </p>
    </div>
  );
}

function Section({ icon, title, intro, children }: { icon: string; title: string; intro: string; children: ReactNode }) {
  return (
    <forge-card>
      <div className="stack gap-small">
        <h2 className="forge-typography--heading3 row gap-xsmall"><forge-icon name={icon} />{title}</h2>
        <p className="forge-typography--body2">{intro}</p>
        <div className="table-wrap">{children}</div>
      </div>
    </forge-card>
  );
}

const Limits = () => <th className="num" title="The most a model reads in one request / the longest reply it can write">Context / max output</th>;

function LimitCells({ e }: { e: CatalogEntry }) {
  if (e.contextWindow === null && e.maxOutput === null) {
    return <td className="num muted" title="The gateway does not publish these figures">not published</td>;
  }
  return <td className="num">{tokenWindow(e.contextWindow) ?? "?"} / {tokenWindow(e.maxOutput) ?? "?"}</td>;
}

function PriceCell({ e }: { e: CatalogEntry }) {
  if (!e.price) return <td className="muted">not published</td>;
  const p = priceLines(e.price);
  return <td className="wrap-ok price">{p.main}{p.cache && <div className="muted">{p.cache}</div>}</td>;
}
